import { useCallback, useEffect, useState } from "react";
import { DEFAULT_LIST_PAGE_SIZE } from "../constants/listPagination";
import { fetchMyGeneratedContracts } from "../services/generatedContractsService";
import { mapGeneratedContractToMyContract } from "../utils/mapGeneratedContractToMyContract";
import type { MyContract } from "../types/myContract";

export type UseMyContractsOptions = {
  /** 1-based page */
  readonly page: number;
  readonly perPage: number;
  readonly contract_type?: string;
  readonly contract_origination?: string;
  readonly business_unit?: string;
  readonly q?: string;
  readonly status?: readonly string[];
};

export type UseMyContractsResult = {
  readonly contracts: MyContract[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly reload: () => void;
  /** `meta.total` from the list API */
  readonly total: number;
};

export function useMyContracts(options: UseMyContractsOptions): UseMyContractsResult {
  const { page, perPage, contract_type, contract_origination, business_unit, q, status } =
    options;
  const [contracts, setContracts] = useState<MyContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const limit = perPage;
        const offset = (page - 1) * perPage;
        const { items, total: apiTotal } = await fetchMyGeneratedContracts({
          limit,
          offset,
          contract_type: contract_type?.trim() || undefined,
          contract_origination: contract_origination?.trim() || undefined,
          business_unit: business_unit?.trim() || undefined,
          q: q?.trim() || undefined,
          status: status?.length ? status : undefined
        });
        if (cancelled) return;
        const mapped = items.map((row) => mapGeneratedContractToMyContract(row));
        setContracts(mapped);
        setTotal(apiTotal);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Failed to load contracts";
        setError(msg);
        setContracts([]);
        setTotal(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    reloadKey,
    page,
    perPage,
    contract_type,
    contract_origination,
    business_unit,
    q,
    status
  ]);

  return { contracts, loading, error, reload, total };
}

export { DEFAULT_LIST_PAGE_SIZE };
